import React from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import SettingsContainer from "@ats/src/components/shared/SettingsContainer";
import FormSection from "@ats/src/components/forms/FormSection";
import Button from "@ats/src/components/shared/Button";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import FriendlyErrors from "@ats/src/components/shared/FriendlyErrors";
import Icon from "@ats/src/components/shared/Icon";
import EmptyState from "@ats/src/components/shared/EmptyState";

import { useToastContext } from "@shared/context/ToastContext";
import { useModalContext } from "@shared/context/ModalContext";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useApiKey, useCreateApiKey, useDeleteApiKey } from "@shared/queryHooks/useApiKey";
import { useFeatureFlipper, Features } from "@ats/src/components/shared/FeatureFlipper";

import ApiKeyCreatedModal from "@ats/src/components/modals/ApiKeyCreatedModal";
import ConfirmationModal from "@ats/src/components/modals/ConfirmationModal";

interface Props {
  setIsDirty: (isDirty: boolean) => void;
  history: any;
}

function AccountApiKeys(props: Props) {
  const { history } = props;
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();
  const { currentOrganizationUser } = useCurrentSession();

  const isCustomerApiEnabled = useFeatureFlipper()({ feature: Features.CUSTOMER_API });

  const { data, isLoading: isLoadingApiKey } = useApiKey();
  const { mutate: createApiKey, isLoading: isLoadingCreate } = useCreateApiKey();
  const { mutate: deleteApiKey, isLoading: isLoadingDelete } = useDeleteApiKey();

  const apiKey = data;
  const hasExistingKey = !!apiKey?.keyHint;

  window.logger("%c[AccountApiKeys] render", "background-color: #3B82F6", {
    data,
    apiKey,
    hasExistingKey,
    isLoadingApiKey,
  });

  // Check if user is admin
  if (!currentOrganizationUser.isAdmin) {
    return <FriendlyErrors statusCode={403} resource="App Settings" actionUrl="/hire/settings/preferences" actionLabel="settings" />;
  }

  if (!isCustomerApiEnabled) {
    const redirectPath = currentOrganizationUser.role === "org_user"
      ? "/hire/settings/preferences"
      : "/hire/settings/organization";
    history.push(redirectPath);
    return null;
  }

  if (isLoadingApiKey) {
    return <LoadingIndicator label="Loading..." />;
  }

  const handleGenerateKey = (e?: React.MouseEvent) => {
    e?.preventDefault();
    createApiKey(null, {
      onSuccess: (data: any) => {
        const modal = (
          <ApiKeyCreatedModal
            apiKey={data.key}
            onCancel={removeModal}
          />
        );
        openModal(modal);
      },
      onError: () => {
        addToast({
          title: "Could not generate API key",
          kind: "warning",
        });
      },
    });
  };

  const handleRegenerateKey = (e?: React.MouseEvent) => {
    e?.preventDefault();
    const modal = (
      <ConfirmationModal
        title="Regenerate API key?"
        subcopy="This will permanently revoke your current API key. Any integrations using it will immediately stop working."
        confirmText="Regenerate key"
        cancelText="Keep current key"
        onConfirm={() => {
          removeModal();
          createApiKey(null, {
            onSuccess: (data: any) => {
              openModal(
                <ApiKeyCreatedModal apiKey={data.key} onCancel={removeModal} />
              );
            },
            onError: () => {
              addToast({ title: "Could not generate API key", kind: "warning" });
            },
          });
        }}
        onCancel={removeModal}
        isDestructiveConfirm={true}
      />
    );
    openModal(modal);
  };

  const handleRevokeKey = (e?: React.MouseEvent) => {
    e?.preventDefault();
    const modal = (
      <ConfirmationModal
        title="Revoke API key?"
        subcopy="This action cannot be undone. Any integrations using this key will immediately stop working."
        confirmText="Revoke key"
        cancelText="Keep key"
        onConfirm={() => {
          removeModal();
          deleteApiKey(null, {
            onSuccess: () => {
              addToast({
                title: "API key revoked",
                kind: "success",
              });
            },
            onError: () => {
              addToast({
                title: "Could not revoke API key",
                kind: "warning",
              });
            },
          });
        }}
        onCancel={removeModal}
        isDestructiveConfirm={true}
      />
    );
    openModal(modal);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const viewLink = (
    <Button
      type="externalLink"
      link="https://developer.polymer.co/#introduction"
      styleType="text"
    >
      View docs
      <Icon name="external-link" />
    </Button>
  );

  return (
    <>
      <Helmet title="Your API key" />
      <SettingsContainer
        title="Your API key"
        actions={viewLink}
        description="Manage your API key for accessing the Polymer Customer API."
      >
        <FormSection>
          {hasExistingKey ? (
            <>
              <Styled.Container>
                <Styled.KeyRow>
                  <Styled.KeyLabel>Active API Key</Styled.KeyLabel>
                  <Styled.KeyHint>••••••••••••••••••••••••{apiKey.keyHint}</Styled.KeyHint>
                </Styled.KeyRow>
                <Styled.MetaInfo>
                  <span>Created {formatDate(apiKey.createdAt)}</span>
                  <Styled.Separator>·</Styled.Separator>
                  <span>Last used {formatDate(apiKey.lastUsedAt)}</span>
                </Styled.MetaInfo>
              </Styled.Container>
              <Styled.ButtonWrapper>
                <Button styleType="secondary" onClick={handleRegenerateKey} loading={isLoadingCreate}>
                  Regenerate key
                </Button>
                <Button styleType="secondary" onClick={handleRevokeKey} loading={isLoadingDelete}>
                  Revoke key
                </Button>
              </Styled.ButtonWrapper>
            </>
          ) : (
            <>
              <Styled.EmptyStateWrapper>
                <EmptyState
                  icon="key"
                  title="No API key"
                  message="Generate one to start using the Polymer Customer API."
                />
              </Styled.EmptyStateWrapper>
              <Button styleType="secondary" onClick={handleGenerateKey} loading={isLoadingCreate}>
                Generate API key
              </Button>
            </>
          )}
        </FormSection>
      </SettingsContainer>
    </>
  );
}

export default AccountApiKeys;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_Container;
    ${[t.pl(4), t.pr(3), t.py(3), t.my(8), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});

Styled.KeyRow = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_KeyRow;
    ${t.mb(2)}
    display: flex;
    justify-content: space-between;
    align-items: center;
  `;
});

Styled.KeyLabel = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_KeyLabel;
    color: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.KeyHint = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_KeyHint;
    font-family: monospace;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.MetaInfo = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_MetaInfo;
    ${[t.text.xs]}
    display: flex;
    align-items: center;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Separator = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_Separator;
    ${[t.mx(2)]}
  `;
});

Styled.ButtonWrapper = styled.div(() => {
  return css`
    label: AccountApiKeys_ButtonWrapper;
    display: flex;
    gap: 0.5rem;
  `;
});

Styled.EmptyStateWrapper = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AccountApiKeys_EmptyStateWrapper;
    ${t.my(8)}
  `;
});
